/**
 * Users — admins only (2026-10-08, roles ported from Device Lab). Who can sign in and as what: Google
 * accounts appear after their first sign-in, password accounts are created here. The role select saves
 * on change; a password is set in a small inline form, never shown back; delete uses the hold-to-confirm
 * dialog. Every refusal (`last_admin`, `user_exists`, `wrong_domain`, `weak_password`) is shown in the
 * service's own words.
 *
 * The navigation shows this page to admins only — the one deliberate "hide instead of disable", as
 * Device Lab's `/keys`: there is nothing here a tester could ask an admin for. The route still answers a
 * link with who to ask, because a link should not 404.
 */

import { useState, type FormEvent } from 'react'

import type { UserRow } from '../../api/types'
import { useT } from '../../i18n'
import { useApp } from '../../lib/context'
import { usePoll } from '../../lib/poll'
import { toast } from '../../lib/toast'
import { ago, exactly } from '../../lib/when'
import { Card, Empty, Pill, Skeletons, Status } from '../../ui/components'
import { ConfirmDanger } from '../../ui/ConfirmDanger'
import { useDirectionName, useDirections } from '../marketing/directions'

const MIN_PASSWORD = 8

export function UsersPanel() {
  const { api, auth, authEpoch } = useApp()
  const t = useT()
  const u = t.users
  const users = usePoll(() => api.users(), 30000, [api, authEpoch], auth.isAdmin)
  const [busy, setBusy] = useState(false)
  const [problem, setProblem] = useState<string | null>(null)
  const [toDelete, setToDelete] = useState<UserRow | null>(null)
  const [passwordFor, setPasswordFor] = useState<string | null>(null)

  if (!auth.isAdmin) {
    return <Card title={u.title}><Status text={u.adminOnly} bad /></Card>
  }

  const run = async (fn: () => Promise<unknown>, done: string) => {
    setProblem(null)
    setBusy(true)
    try {
      await fn()
      toast(done)
      users.refresh()
      return true
    } catch (e) {
      setProblem(e instanceof Error ? e.message : String(e))
      return false
    } finally {
      setBusy(false)
    }
  }

  const me = auth.user?.email ?? null
  const roles = users.data?.roles ?? ['tester', 'admin']
  const roleName = (role: string) => u.roles[role] ?? role
  const dirs = useDirections()
  const nameOf = useDirectionName()

  return (
    <>
      <Card title={u.title} sub={u.sub}>
        <p className="dim small">{u.roleHint}</p>
        <Status text={problem ?? (users.error ? u.readError(users.error.message) : null)} bad />
        {!users.data && !users.error && <Skeletons count={2} />}
        {users.data && users.data.users.length === 0 && <Empty>{u.none}</Empty>}
        {users.data && users.data.users.length > 0 && (
          <div className="scroll-x">
            <table className="grid">
              <thead>
                <tr>
                  <th>{u.email}</th>
                  <th>{u.name}</th>
                  <th>{u.role}</th>
                  <th>{u.directions}</th>
                  <th>{u.createdAt}</th>
                  <th>{u.signIn}</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {users.data.users.map((row) => (
                  <tr key={row.email}>
                    <td>
                      <b>{row.email}</b>
                      {row.email === me && <> <Pill>{u.you}</Pill></>}
                    </td>
                    <td className="dim small">{row.name || t.common.dash}</td>
                    <td>
                      <select value={row.role} disabled={busy} aria-label={u.role}
                              onChange={(e) => void run(() => api.editUser(row.email, { role: e.target.value }),
                                                         u.roleSaved(row.email, roleName(e.target.value)))}>
                        {roles.map((r) => <option key={r} value={r}>{roleName(r)}</option>)}
                      </select>
                    </td>
                    <td>
                      {/* the directions this person uploads, pushes and posts in — a toggle per direction;
                          an admin acts in every one regardless (owner, 2026-10-12) */}
                      {row.role === 'admin' ? <span className="tiny dim">{u.allDirections}</span> : (
                        <span className="user-dirs">
                          {dirs.list.map((d) => {
                            const on = (row.directions ?? []).includes(d.direction_id)
                            const next = on ? (row.directions ?? []).filter((x) => x !== d.direction_id)
                                            : [...(row.directions ?? []), d.direction_id]
                            return (
                              <button key={d.direction_id} type="button" disabled={busy} aria-pressed={on}
                                      className={`dir-toggle${on ? ' on' : ''}`} data-color={d.color}
                                      onClick={() => void run(() => api.editUser(row.email, { directions: next }),
                                                              u.directionsSaved(row.email))}>
                                {nameOf(d)}
                              </button>
                            )
                          })}
                          {(row.directions ?? []).length === 0 && <span className="tiny warn-text">{u.noDirections}</span>}
                        </span>
                      )}
                    </td>
                    <td className="dim small" title={exactly(row.created_at)}>{ago(row.created_at)}</td>
                    <td className="dim small">{row.has_password ? u.password : u.googleOnly}</td>
                    <td style={{ textAlign: 'right' }}>
                      {passwordFor === row.email ? (
                        <PasswordForm
                          busy={busy}
                          onCancel={() => setPasswordFor(null)}
                          onSave={async (password) => {
                            if (await run(() => api.editUser(row.email, { password }), u.passwordSet(row.email))) setPasswordFor(null)
                          }}
                        />
                      ) : (
                        <>
                          <button className="ghost btn-sm" disabled={busy} onClick={() => setPasswordFor(row.email)}>
                            {u.resetPassword}
                          </button>{' '}
                          <button className="danger btn-sm" disabled={busy || row.email === me}
                                  title={row.email === me ? u.you : u.delete} onClick={() => setToDelete(row)}>
                            {u.delete}
                          </button>
                        </>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <AddUser roles={roles} roleName={roleName} busy={busy}
               onCreate={(body) => run(() => api.createUser(body), u.created(body.email))} />

      {toDelete && (
        <ConfirmDanger
          title={u.deleteTitle(toDelete.email)}
          confirmLabel={u.delete}
          holdSeconds={2}
          onCancel={() => setToDelete(null)}
          onConfirm={() => {
            const gone = toDelete
            setToDelete(null)
            void run(() => api.deleteUser(gone.email), u.deleted(gone.email))
          }}
        >
          <p className="dim small">{u.deleteBody}</p>
        </ConfirmDanger>
      )}
    </>
  )
}

/** A new password typed once, in the row: the service refuses a short one (`weak_password`), so do we. */
function PasswordForm({ busy, onSave, onCancel }: { busy: boolean; onSave: (password: string) => void; onCancel: () => void }) {
  const u = useT().users
  const [password, setPassword] = useState('')
  const ok = password.length >= MIN_PASSWORD
  return (
    <form className="row" style={{ justifyContent: 'flex-end' }}
          onSubmit={(e) => { e.preventDefault(); if (ok) onSave(password) }}>
      <input type="password" autoFocus autoComplete="new-password" value={password} placeholder={u.newPassword}
             aria-label={u.newPassword} title={u.minPassword} style={{ width: 180 }}
             onChange={(e) => setPassword(e.target.value)}
             onKeyDown={(e) => { if (e.key === 'Escape') onCancel() }} />
      <button className="primary btn-sm" type="submit" disabled={busy || !ok} title={ok ? u.save : u.minPassword}>{u.save}</button>
      <button className="ghost btn-sm" type="button" onClick={onCancel}>{u.cancel}</button>
    </form>
  )
}

function AddUser({ roles, roleName, busy, onCreate }: {
  roles: string[]
  roleName: (role: string) => string
  busy: boolean
  onCreate: (body: { email: string; password: string; role: string; name?: string }) => Promise<boolean>
}) {
  const u = useT().users
  const [email, setEmail] = useState('')
  const [name, setName] = useState('')
  const [password, setPassword] = useState('')
  const [role, setRole] = useState('tester')
  const ok = email.trim().length > 0 && password.length >= MIN_PASSWORD

  const submit = async (e: FormEvent) => {
    e.preventDefault()
    if (!ok) return
    if (await onCreate({ email: email.trim(), password, role, name: name.trim() || undefined })) {
      setEmail('')
      setName('')
      setPassword('')
    }
  }

  return (
    <Card title={u.addTitle} sub={u.addSub}>
      <form className="row" onSubmit={(e) => void submit(e)}>
        <input type="email" required autoComplete="off" value={email} placeholder="name@mornhouse.com"
               aria-label={u.email} onChange={(e) => setEmail(e.target.value)} />
        <input value={name} placeholder={u.name} aria-label={u.name} onChange={(e) => setName(e.target.value)} />
        <input type="password" autoComplete="new-password" value={password} placeholder={u.newPassword}
               aria-label={u.newPassword} title={u.minPassword} onChange={(e) => setPassword(e.target.value)} />
        <select value={role} aria-label={u.role} onChange={(e) => setRole(e.target.value)}>
          {roles.map((r) => <option key={r} value={r}>{roleName(r)}</option>)}
        </select>
        <button className="primary" type="submit" disabled={busy || !ok} title={ok ? u.add : u.minPassword}>{u.add}</button>
      </form>
    </Card>
  )
}
